import type { ProductDeleteConfirmationModalProps } from "@v2/types/modalTypes";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";

import { useProductDelete } from "../hooks/useProductDelete";

export function ProductDeleteConfirmationModal({
  productId,
  projectId,
  open,
  onOpenChange,
}: ProductDeleteConfirmationModalProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const { deleteProduct, isLoading } = useProductDelete();
  const navigate = useNavigate();

  const handleDelete = async () => {
    await deleteProduct(productId);
    onOpenChange(false);
    navigate(
      generatePath(routes.shops.products.list, {
        projectId,
      }),
    );
  };

  const description =
    "You will not be able to undo this action. All pages with this product will not be connected to product.";

  const footer = (
    <div className="flex justify-end w-full">
      <Button
        variant="destructive"
        size="base"
        onClick={() => void handleDelete()}
        loading={isLoading}
      >
        Delete Permanently
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Delete Product"
        description={description}
        footer={footer}
      />
    );
  }

  return (
    <Modal
      size="sm"
      open={open}
      onOpenChange={onOpenChange}
      title="Delete Product"
      description={description}
      footer={footer}
    />
  );
}
